import React, { useEffect, useState } from 'react';
import axios from 'axios';

function DashboardCard03({ stockSymbol }) {
  const [companyInfo, setCompanyInfo] = useState(null); // State to hold company information
  const [loading, setLoading] = useState(true); // Loading state
  const [currentPrice, setCurrentPrice] = useState(0); // State to hold current price
  const [currency, setCurrency] = useState(""); // State to hold currency

  // Helper function to format market capitalization
  const formatMarketCap = (value) => {
    if (!value) return 'N/A';
    
    const num = parseFloat(value);
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`; // Trillions
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;   // Billions
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;   // Millions
    return num.toFixed(2); // Less than a million
  };

  // Helper function to format shares outstanding
  const formatSharesOutstanding = (value) => {
    if (!value) return 'N/A';

    // Convert to string, insert period after the first digit and then every 3 digits
    const numStr = value.toString();
    const formatted = `${numStr[0]}.${numStr.slice(1).replace(/(\d{3})(?=\d)/g, '$1.')}`;
    return formatted;
  };

  // Fetch company information
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        console.log(`Fetching company info for: ${stockSymbol}`); // Log the stock symbol
  
        const response = await axios.get(`https://investorverden.onrender.com/api/financials?q=${stockSymbol}`);
        const stockPrice = await axios.get(`https://investorverden.onrender.com/api/stock-price?q=${stockSymbol}`);
        
        console.log('API response latest:', response.data); // Log the API response
        console.log('API response latest price:', stockPrice); // Log the API response
        setCurrentPrice(stockPrice.data.currentPrice);
        setCompanyInfo(response.data);
        setCurrency(response.data.summaryDetail.currency); // Set the currency
        setLoading(false);
      } catch (error) {
        console.error('Error fetching company information:', error);
        setLoading(false);
      }
    };

    if (stockSymbol) {
      fetchCompanyInfo();
    } else {
      console.error('No stockSymbol provided');
    }
  }, [stockSymbol]);

  // Check if loading or company info is not yet available
  if (loading) {
    return <div>Loader...</div>;
  }

  const formatMarketCapWithCurrency = (value, currencySymbol) => {
    if (!value) return 'N/A';

    const num = parseFloat(value);
    let formattedValue = '';
    if (num >= 1e12) formattedValue = `${(num / 1e12).toFixed(2)}T`;
    else if (num >= 1e9) formattedValue = `${(num / 1e9).toFixed(2)}B`;
    else if (num >= 1e6) formattedValue = `${(num / 1e6).toFixed(2)}M`;
    else formattedValue = num.toFixed(2);
    if (!currencySymbol) return formattedValue;
    return `${currencySymbol}${formattedValue}`;
  };

  return (
    <div className="flex flex-col col-span-full sm:col-span-6 xl:col-span-6 bg-white dark:bg-gray-800 shadow-sm rounded-xl">
      <div className="px-5 pt-5">
        <header className="flex justify-between items-start mb-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
            {stockSymbol || 'No Symbol'}
          </h2>
        </header>
        <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-1">Nøgletals Data</div>
        <div className="flex flex-col space-y-1">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Børsværdi:</strong> {formatMarketCapWithCurrency(companyInfo.summaryDetail.marketCap, currency) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>EPS:</strong> {companyInfo.defaultKeyStatistics.trailingEps}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Antal aktier:</strong> {formatSharesOutstanding(companyInfo.defaultKeyStatistics.sharesOutstanding) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Nuværende PE:</strong> {(companyInfo?.summaryDetail?.trailingPE?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Estimeret PE:</strong>  {(companyInfo?.summaryDetail?.forwardPE?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>P/B:</strong>  {(companyInfo?.defaultKeyStatistics?.priceToBook?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>P/S:</strong> {(companyInfo?.summaryDetail?.priceToSalesTrailing12Months?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>regularMarketDayLow:</strong>  {(companyInfo?.summaryDetail?.regularMarketDayLow?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>regularMarketDayHigh:</strong>  {(companyInfo?.summaryDetail?.regularMarketDayHigh?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>regularMarketOpen:</strong>  {(companyInfo?.summaryDetail?.regularMarketOpen?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>volume:</strong>  {(companyInfo?.summaryDetail?.volume?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>regularMarketVolume:</strong>  {(companyInfo?.summaryDetail?.regularMarketVolume?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>averageVolume:</strong>  {(companyInfo?.summaryDetail?.averageVolume?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>averageVolume10days:</strong>  {(companyInfo?.summaryDetail?.averageVolume10days?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>fiftyTwoWeekLow:</strong>  {(companyInfo?.summaryDetail?.fiftyTwoWeekLow?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>fiftyTwoWeekHigh:</strong>  {(companyInfo?.summaryDetail?.fiftyTwoWeekHigh?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>fiftyDayAverage:</strong>  {(companyInfo?.summaryDetail?.fiftyDayAverage?.toFixed(2)) || 'N/A'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>twoHundredDayAverage:</strong>  {(companyInfo?.summaryDetail?.twoHundredDayAverage?.toFixed(2)) || 'N/A'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardCard03;